/**
 * Mock commissions, attributed services and claims for «سارا محمدی». 29 commissions covering every
 * status and most reason codes; deterministic; dates relative to now; money as rial strings. Every
 * factory returns exactly the shared response type (`@mfa-aff/shared`, packages/shared/src/api/partner.ts).
 */
import {
    COMMISSION_TIMELINE_FA,
    type ChannelKey,
    type ClaimView,
    type CommissionExplainView,
    type CommissionItem,
    type CommissionStatusKey,
    type CommissionTimelineStep,
    type CommissionTimelineView,
    type CreateClaimRequest,
    type EventKey,
    type LegacyCommissionItem,
    type LegacyCommissionsExport,
    type LegacyCommissionsFilter,
    type LegacyCommissionsResponse,
    type LegacyPayoutsResponse,
    type PartnerClaimsResponse,
    type PartnerCommissionsResponse,
    type PartnerServiceItem,
    type PartnerServicesResponse,
    type TimelineStepState,
    type TimelineStepView,
    type UiCommissionStatus,
    reasonNextStep,
    reasonText,
} from "@mfa-aff/shared";
import { type ReasonCode, formatJalali, formatPercentBp, formatToman, maskDomain, toPersianDigits } from "@mfa-aff/ui";
import { ApiError } from "../api/client";
import type { CommissionFilters } from "../api/orders";

const DAY = 86_400_000;
const now = Date.now();
const at = (daysAgo: number, hour = 11) => new Date(now - daysAgo * DAY + (hour - 12) * 3_600_000).toISOString();
const plus = (iso: string, days: number) => new Date(new Date(iso).getTime() + days * DAY).toISOString();
const fa = (n: number) => toPersianDigits(String(n));

/** Seeds use the old shorthand: `renewal` is a renewal sale, `approved`/`available`/`paid` the stored states. */
type SeedChannel = ChannelKey | "renewal";
type SeedStatus = "pending" | "approved" | "available" | "paid" | "held" | "reversed" | "void";

const CHANNEL_FA: Record<ChannelKey, string> = { link: "لینک", code: "کد تخفیف", refcode: "کد معرف", manual: "تخصیص دستی", campaign: "کمپین" };
const RULE_STEP_FA: Record<number, string> = {
    0: "تخصیص دستی تیم میزبان‌فا",
    1: "تمدید سرویسی که قبلاً از طریق شما خریده شده",
    2: "سفارش فقط دامنه",
    3: "کد تخفیف شما",
    4: "کد معرف شما",
    5: "کلیک روی لینک شما",
    6: "مشتری متصل به شما (خرید قبلی از طریق شما)",
    7: "بدون نشانه از شما",
};
const UI_STATUS: Record<CommissionStatusKey, UiCommissionStatus> = {
    pending: "pending",
    approved: "available",
    paid: "paid_out",
    held: "held",
    reversed: "reversed",
    void: "void",
};
const STATUS_FA: Record<UiCommissionStatus, string> = {
    pending: "در دورهٔ ضمانت",
    available: "قابل برداشت",
    paid_out: "پرداخت‌شده",
    held: "در حال بررسی",
    reversed: "برگشت خورد",
    void: "بدون پورسانت",
};

type Seed = {
    d: number;
    product: string;
    group: string;
    domain: string;
    channel: SeedChannel;
    step: number;
    status: SeedStatus;
    /** Base in toman. */
    toman: number;
    rate: number;
    discount?: number;
    event?: EventKey;
    reason?: ReasonCode;
    orderId?: number;
    payout?: "wallet" | "bank";
    signals?: string[];
};

const seeds: Seed[] = [
    {
        d: 3,
        product: "هاست وردپرس حرفه‌ای، سالانه",
        group: "هاست وردپرس",
        domain: "kafeketab.ir",
        channel: "code",
        step: 3,
        status: "pending",
        toman: 3_400_000,
        rate: 2200,
        discount: 800,
    },
    {
        d: 2,
        product: "هاست وردپرس حرفه‌ای، تمدید سالانه",
        group: "هاست وردپرس",
        domain: "parsdent.com",
        channel: "renewal",
        step: 1,
        status: "pending",
        toman: 3_400_000,
        rate: 800,
        event: "renewal",
    },
    {
        d: 6,
        product: "سرور مجازی ابری، ۴ گیگ",
        group: "سرور مجازی ابری",
        domain: "novinlab.com",
        channel: "link",
        step: 5,
        status: "pending",
        toman: 5_200_000,
        rate: 2500,
    },
    {
        d: 9,
        product: "هاست لینوکس اقتصادی، سالانه",
        group: "هاست لینوکس",
        domain: "golbarg-gallery.ir",
        channel: "refcode",
        step: 4,
        status: "pending",
        toman: 1_200_000,
        rate: 2200,
    },
    {
        d: 11,
        product: "هاست ووکامرس، سالانه",
        group: "هاست ووکامرس",
        domain: "shirinshop.ir",
        channel: "link",
        step: 6,
        status: "pending",
        toman: 4_800_000,
        rate: 2500,
    },
    {
        d: 8,
        product: "سرور مجازی ابری، ۸ گیگ",
        group: "سرور مجازی ابری",
        domain: "datakavan.net",
        channel: "link",
        step: 5,
        status: "held",
        toman: 9_600_000,
        rate: 2500,
        reason: "UNDER_REVIEW",
    },
    {
        d: 12,
        product: "هاست وردپرس پایه، سالانه",
        group: "هاست وردپرس",
        domain: "rozaneh.ir",
        channel: "refcode",
        step: 4,
        status: "held",
        toman: 2_100_000,
        rate: 2200,
        reason: "UNDER_REVIEW",
    },
    {
        d: 14,
        product: "هاست وردپرس حرفه‌ای، سالانه",
        group: "هاست وردپرس",
        domain: "saramohammadi.ir",
        channel: "code",
        step: 3,
        status: "void",
        toman: 3_400_000,
        rate: 2200,
        discount: 800,
        reason: "SELF_REFERRAL",
    },
    {
        d: 16,
        product: "هاست وردپرس پایه، سالانه",
        group: "هاست وردپرس",
        domain: "mehrabcafe.ir",
        channel: "code",
        step: 3,
        status: "approved",
        toman: 2_100_000,
        rate: 2200,
        discount: 500,
    },
    {
        d: 18,
        product: "هاست لینوکس حرفه‌ای، سالانه",
        group: "هاست لینوکس",
        domain: "atlasclinic.ir",
        channel: "link",
        step: 3,
        status: "void",
        toman: 2_600_000,
        rate: 2500,
        reason: "ATTRIBUTED_TO_OTHER_CODE",
        signals: [
            "کلیک روی لینک شما ۴ روز پیش از خرید ثبت شده بود.",
            "در صفحهٔ پرداخت کد تخفیف بازاریاب دیگری وارد شد؛ در جدول اولویت، کد تخفیف بالاتر از کلیک است (گام ۳).",
        ],
    },
    {
        d: 19,
        product: "لایسنس سی‌پنل، ماهانه",
        group: "افزودنی‌ها",
        domain: "tarhenoo.com",
        channel: "link",
        step: 5,
        status: "void",
        toman: 900_000,
        rate: 0,
        reason: "NOT_ELIGIBLE_PRODUCT",
    },
    {
        d: 21,
        product: "سرور مجازی ابری، ۴ گیگ",
        group: "سرور مجازی ابری",
        domain: "novinlab.com",
        channel: "link",
        step: 5,
        status: "available",
        toman: 5_200_000,
        rate: 2500,
    },
    {
        d: 22,
        product: "هاست وردپرس حرفه‌ای، سالانه",
        group: "هاست وردپرس",
        domain: "honarkadeh.ir",
        channel: "link",
        step: 6,
        status: "void",
        toman: 3_400_000,
        rate: 2500,
        reason: "BOUND_TO_OTHER_AFFILIATE",
        signals: ["کلیک روی لینک شما ثبت شده بود، اما این مشتری پیش‌تر از طریق بازاریاب دیگری خرید کرده و هنوز در بازهٔ اتصال اوست."],
    },
    {
        d: 25,
        product: "هاست لینوکس اقتصادی، تمدید سالانه",
        group: "هاست لینوکس",
        domain: "shahrvand-news.ir",
        channel: "renewal",
        step: 1,
        status: "available",
        toman: 1_800_000,
        rate: 800,
        event: "renewal",
    },
    {
        d: 27,
        product: "هاست وردپرس حرفه‌ای، سالانه",
        group: "هاست وردپرس",
        domain: "arvinsoft.ir",
        channel: "link",
        step: 5,
        status: "reversed",
        toman: 3_400_000,
        rate: 2500,
        reason: "REFUNDED_IN_GUARANTEE",
    },
    {
        d: 30,
        product: "آسمان آبی، پلن استارتر",
        group: "آسمان آبی",
        domain: "pixelyar.com",
        channel: "campaign",
        step: 4,
        status: "available",
        toman: 2_400_000,
        rate: 1200,
    },
    {
        d: 30,
        product: "ثبت دامنهٔ ‎.ir",
        group: "دامنه",
        domain: "melodyland.ir",
        channel: "code",
        step: 2,
        status: "void",
        toman: 0,
        rate: 0,
        reason: "DOMAIN_ONLY",
    },
    {
        d: 35,
        product: "هاست وردپرس پایه، تمدید سالانه",
        group: "هاست وردپرس",
        domain: "baharestan-co.ir",
        channel: "renewal",
        step: 1,
        status: "void",
        toman: 2_100_000,
        rate: 800,
        event: "renewal",
        reason: "SERVICE_NOT_ACTIVE",
    },
    {
        d: 40,
        product: "سرور مجازی ابری، ۲ گیگ",
        group: "سرور مجازی ابری",
        domain: "farazdev.ir",
        channel: "link",
        step: 5,
        status: "paid",
        toman: 3_100_000,
        rate: 2500,
        payout: "wallet",
    },
    {
        d: 44,
        product: "هاست وردپرس پایه، سالانه",
        group: "هاست وردپرس",
        domain: "nazkhatoon.ir",
        channel: "code",
        step: 3,
        status: "void",
        toman: 2_100_000,
        rate: 2200,
        discount: 500,
        reason: "CODE_LIMIT_REACHED",
    },
    {
        d: 48,
        product: "هاست ووکامرس، سالانه",
        group: "هاست ووکامرس",
        domain: "chaharfasl.shop",
        channel: "code",
        step: 3,
        status: "reversed",
        toman: 4_800_000,
        rate: 2200,
        discount: 800,
        reason: "REFUNDED_AFTER_APPROVAL",
    },
    {
        d: 52,
        product: "هاست وردپرس حرفه‌ای، سالانه",
        group: "هاست وردپرس",
        domain: "ketabyar.ir",
        channel: "code",
        step: 3,
        status: "paid",
        toman: 3_400_000,
        rate: 2200,
        discount: 800,
        payout: "wallet",
    },
    {
        d: 60,
        product: "هاست لینوکس اقتصادی، تمدید سالانه",
        group: "هاست لینوکس",
        domain: "sharifprint.ir",
        channel: "renewal",
        step: 1,
        status: "paid",
        toman: 1_800_000,
        rate: 800,
        event: "renewal",
        payout: "wallet",
    },
    {
        d: 24,
        product: "هاست وردپرس حرفه‌ای، سالانه",
        group: "هاست وردپرس",
        domain: "danesh-yar.ir",
        channel: "manual",
        step: 0,
        status: "available",
        toman: 3_400_000,
        rate: 2500,
        orderId: 48211,
    },
    {
        d: 95,
        product: "سرور مجازی ابری، ۴ گیگ",
        group: "سرور مجازی ابری",
        domain: "mahtabco.com",
        channel: "link",
        step: 5,
        status: "paid",
        toman: 5_200_000,
        rate: 2500,
        payout: "bank",
    },
    {
        d: 120,
        product: "هاست وردپرس پایه، سالانه",
        group: "هاست وردپرس",
        domain: "royayesabz.ir",
        channel: "refcode",
        step: 4,
        status: "paid",
        toman: 2_100_000,
        rate: 2200,
        payout: "wallet",
    },
    {
        d: 180,
        product: "هاست لینوکس حرفه‌ای، سالانه",
        group: "هاست لینوکس",
        domain: "iranmehr.org",
        channel: "link",
        step: 5,
        status: "paid",
        toman: 2_600_000,
        rate: 2000,
        payout: "wallet",
    },
    {
        d: 210,
        product: "هاست وردپرس حرفه‌ای، تمدید سالانه",
        group: "هاست وردپرس",
        domain: "parsdent.com",
        channel: "renewal",
        step: 1,
        status: "paid",
        toman: 3_200_000,
        rate: 600,
        event: "renewal",
        payout: "bank",
    },
    {
        d: 260,
        product: "هاست وردپرس حرفه‌ای، سالانه",
        group: "هاست وردپرس",
        domain: "parsdent.com",
        channel: "code",
        step: 3,
        status: "paid",
        toman: 3_200_000,
        rate: 1800,
        discount: 800,
        payout: "wallet",
    },
];


const levelAt = (d: number) => (d > 150 ? "برنزی" : "نقره‌ای");

function step(
    key: CommissionTimelineStep,
    state: TimelineStepState,
    atIso: string | null,
    expectedAt: string | null = null,
    note: string | null = null,
    daysLeft: number | null = null,
): TimelineStepView {
    const copy = COMMISSION_TIMELINE_FA[key];
    return {
        key,
        explanationKey: key,
        state,
        label: copy.label,
        explanation: copy.explanation,
        at: atIso,
        atJalali: atIso ? formatJalali(atIso) : null,
        expectedAt,
        expectedAtJalali: expectedAt ? formatJalali(expectedAt) : null,
        daysLeft,
        note,
    };
}

/** Same rules as apps/api partner-portal/timeline.ts (flat fields + ordered steps). */
function timelineOf(
    db: CommissionStatusKey,
    t: { orderedAt: string; paidAt: string; eligibleAt: string; approvedAt: string | null; paidOutAt: string | null; payoutChannel: "wallet" | "bank" | null; branchAt: string | null },
    reason: ReasonCode | null,
): CommissionTimelineView {
    const flat: Omit<CommissionTimelineView, "steps" | "currentStep" | "summary"> = { orderedAt: t.orderedAt, paidAt: t.paidAt, eligibleAt: t.eligibleAt };
    if (db === "approved" || db === "paid") {
        flat.approvedAt = t.approvedAt ?? t.eligibleAt;
        flat.availableAt = flat.approvedAt;
    }
    if (db === "paid") {
        flat.paidOutAt = t.paidOutAt ?? flat.approvedAt;
        if (t.payoutChannel) flat.payoutChannel = t.payoutChannel;
    }
    const steps: TimelineStepView[] = [step("ordered", "done", t.orderedAt), step("paid", "done", t.paidAt)];
    const left = Math.max(0, Math.ceil((new Date(t.eligibleAt).getTime() - now) / DAY));

    if (db === "held" || db === "reversed" || db === "void") {
        const why = reason ? reasonText(reason) : null;
        const branchAt = t.branchAt ?? t.paidAt;
        if (db === "held") {
            steps.push(step("in_guarantee", "skipped", null), step("held", "current", branchAt, null, why ?? COMMISSION_TIMELINE_FA.held.explanation));
        } else {
            steps.push(step("in_guarantee", "done", t.paidAt), step(db, "done", branchAt, null, why));
        }
        for (const k of ["approved", "available", "paid_out"] as const) steps.push(step(k, "skipped", null));
        return {
            ...flat,
            branch: { kind: db, at: branchAt, reason: reason ?? "UNDER_REVIEW" },
            steps,
            currentStep: db,
            summary: why ?? COMMISSION_TIMELINE_FA[db].label,
        };
    }
    if (db === "pending") {
        const note = `دورهٔ ضمانت ${fa(left)} روز دیگر (${formatJalali(t.eligibleAt)}) تمام می‌شود؛ تا آن موقع مشتری می‌تواند انصراف بدهد.`;
        steps.push(
            step("in_guarantee", "current", t.paidAt, t.eligibleAt, note, left),
            step("approved", "upcoming", null, t.eligibleAt),
            step("available", "upcoming", null, t.eligibleAt),
            step("paid_out", "upcoming", null),
        );
        return { ...flat, steps, currentStep: "in_guarantee", summary: `${COMMISSION_TIMELINE_FA.in_guarantee.label}؛ ${fa(left)} روز مانده` };
    }
    const approvedAt = flat.approvedAt!;
    steps.push(step("in_guarantee", "done", t.eligibleAt), step("approved", "done", approvedAt));
    if (db === "paid") {
        steps.push(step("available", "done", approvedAt), step("paid_out", "done", flat.paidOutAt ?? approvedAt));
        return { ...flat, steps, currentStep: "paid_out", summary: t.payoutChannel === "bank" ? "به حساب بانکی شما واریز شد" : "به کیف پول شما واریز شد" };
    }
    steps.push(
        step("available", "current", approvedAt, null, "این مبلغ در موجودی قابل برداشت شماست؛ از بخش «موجودی و برداشت» برداشت کنید یا تسویهٔ خودکار را روشن کنید."),
        step("paid_out", "upcoming", null),
    );
    return { ...flat, steps, currentStep: "available", summary: COMMISSION_TIMELINE_FA.available.label };
}

function explainOf(i: { event: EventKey; channel: ChannelKey; baseRial: bigint; rateBp: number; discountBp: number; amountRial: bigint; clawedBackRial: bigint; levelName: string }): CommissionExplainView {
    const kind = i.event === "renewal" ? "تمدید" : CHANNEL_FA[i.channel];
    const rateLabel = i.channel === "manual" ? `نرخ ${kind}` : `نرخ ${kind} سطح ${i.levelName}`;
    const formula = i.discountBp > 0 ? "code_split" : "rate";
    let sentence = `مبلغ مشمول ${formatToman(i.baseRial, { unit: false })} × ${rateLabel} ${formatPercentBp(i.rateBp)}`;
    if (formula === "code_split") sentence += ` − تخفیفی که شما دادید ${formatPercentBp(i.discountBp)}`;
    sentence += ` = ${formatToman(i.amountRial)}`;
    if (i.clawedBackRial > 0n) {
        sentence += `؛ پس از بازپرداخت مشتری ${formatToman(i.clawedBackRial)} از آن کم شد و ${formatToman(i.amountRial - i.clawedBackRial)} ماند`;
    }
    return {
        formula,
        baseRial: i.baseRial.toString(),
        rateBp: i.rateBp,
        discountBp: i.discountBp,
        discountRial: ((i.baseRial * BigInt(i.discountBp)) / 10_000n).toString(),
        resultRial: i.amountRial.toString(),
        clawedBackRial: i.clawedBackRial.toString(),
        netRial: (i.amountRial - i.clawedBackRial).toString(),
        ruleVersion: 3,
        ruleVersionLabel: "کارت نرخ نسخهٔ ۳",
        rateLabel,
        adjustments: i.channel === "campaign" ? ["نرخ کمپین روی مبلغ خالص پس از تخفیف کمپین حساب شد."] : [],
        sentence,
    };
}

function build(s: Seed, i: number): CommissionItem {
    const paidAt = at(s.d, 9 + (i % 8));
    const orderedAt = new Date(new Date(paidAt).getTime() - 40 * 60_000).toISOString();
    const event: EventKey = s.channel === "renewal" ? "renewal" : (s.event ?? "new");
    const channel: ChannelKey = s.channel === "renewal" ? "link" : s.channel;
    const holdDays = event === "renewal" ? 7 : 15;
    const eligibleAt = plus(paidAt, holdDays);
    const base = BigInt(s.toman) * 10n;
    const discountBp = s.discount ?? 0;
    const full = (base * BigInt(Math.max(s.rate - discountBp, 0))) / 10_000n;
    const zero = s.status === "void" || (s.status === "reversed" && s.reason === "REFUNDED_IN_GUARANTEE");
    const amount = zero ? 0n : full;
    const clawedBack = s.reason === "REFUNDED_AFTER_APPROVAL" ? full / 2n : 0n;
    const db: CommissionStatusKey = s.status === "available" ? "approved" : s.status;
    const status = UI_STATUS[db];
    const reason = s.reason ?? null;
    const approvedAt = db === "approved" || db === "paid" ? plus(eligibleAt, 0.01) : null;
    const branchAt =
        db === "reversed" ? plus(paidAt, s.reason === "REFUNDED_AFTER_APPROVAL" ? 20 : 6) : db === "held" ? plus(paidAt, 1) : db === "void" ? plus(paidAt, 0.02) : null;
    const timeline = timelineOf(
        db,
        {
            orderedAt,
            paidAt,
            eligibleAt,
            approvedAt,
            paidOutAt: db === "paid" ? plus(eligibleAt, Math.min(9, Math.max(1, s.d - holdDays - 2))) : null,
            payoutChannel: db === "paid" ? (s.payout ?? "wallet") : null,
            branchAt,
        },
        reason,
    );
    const orderId = s.orderId ?? 48_400 + (seeds.length - i) * 7;
    return {
        id: String(2000 + i),
        createdAt: paidAt,
        orderId,
        invoiceId: 91_200 + (seeds.length - i) * 9,
        serviceId: s.group === "دامنه" ? null : 70_000 + i * 13,
        product: s.product,
        domainMasked: maskDomain(s.domain),
        event,
        channel,
        channelLabel: event === "renewal" ? "تمدید" : CHANNEL_FA[channel],
        ruleStep: s.step,
        ruleStepLabel: RULE_STEP_FA[s.step] ?? null,
        status,
        dbStatus: db,
        statusLabel: STATUS_FA[status],
        reasonCode: reason,
        reasonText: reason ? reasonText(reason) : null,
        nextStep: reason ? reasonNextStep(reason) : null,
        baseRial: base.toString(),
        rateBp: s.rate,
        discountBp,
        amountRial: amount.toString(),
        timeline,
        explain: explainOf({ event, channel, baseRial: base, rateBp: s.rate, discountBp, amountRial: amount, clawedBackRial: clawedBack, levelName: levelAt(s.d) }),
    };
}

const commissions: CommissionItem[] = seeds.map(build).sort((a, b) => b.createdAt.localeCompare(a.createdAt));

/** The newest commissions (Home «پورسانت‌های اخیر»). */
export const recentCommissionsMock = (n = 5): CommissionItem[] => structuredClone(commissions.slice(0, n));

/** All commissions (release forecast and other mocks derive from them). */
export const allCommissionsMock = (): readonly CommissionItem[] => commissions;

/** `GET commissions?status&channel&days&q&cursor&limit`; the mock cursor is an offset. */
export function commissionsMock(f: CommissionFilters, cursor: string | null, limit: number): PartnerCommissionsResponse {
    const q = f.q.trim().toLowerCase().replace(/^#/, "");
    const minTime = f.range === "all" ? 0 : now - Number(f.range) * DAY;
    const rows = commissions.filter(
        (c) =>
            (f.status === "all" || c.status === f.status) &&
            (f.channel === "all" || (f.channel === "renewal" ? c.event === "renewal" : c.channel === f.channel && c.event !== "renewal")) &&
            new Date(c.createdAt).getTime() >= minTime &&
            (!q || (c.domainMasked ?? "").toLowerCase().includes(q) || c.product.includes(q) || String(c.orderId).includes(q)),
    );
    const start = cursor ? Number(cursor) : 0;
    const items = rows.slice(start, start + limit);
    const counts: Record<UiCommissionStatus, number> = { pending: 0, available: 0, paid_out: 0, held: 0, reversed: 0, void: 0 };
    for (const c of commissions) counts[c.status]++;
    return { items: structuredClone(items), nextCursor: start + limit < rows.length ? String(start + limit) : null, counts };
}

/* ---------------- services ---------------- */

type ServiceSeed = {
    product: string;
    domain: string;
    status: "Active" | "Suspended" | "Cancelled";
    billingCycle: "Monthly" | "Quarterly" | "Semi-Annually" | "Annually";
    channel: ChannelKey;
    startedDaysAgo: number;
    /** Commission earned on it so far, toman. */
    earnedToman: number;
};

const WHMCS_STATUS_FA: Record<ServiceSeed["status"], string> = { Active: "فعال", Suspended: "معلق", Cancelled: "لغوشده" };

const serviceSeeds: ServiceSeed[] = [
    { product: "هاست وردپرس حرفه‌ای", domain: "parsdent.com", status: "Active", billingCycle: "Annually", channel: "code", startedDaysAgo: 363, earnedToman: 748_000 },
    { product: "سرور مجازی ابری، ۴ گیگ", domain: "novinlab.com", status: "Active", billingCycle: "Monthly", channel: "link", startedDaysAgo: 21, earnedToman: 1_300_000 },
    { product: "هاست لینوکس اقتصادی", domain: "shahrvand-news.ir", status: "Active", billingCycle: "Annually", channel: "link", startedDaysAgo: 390, earnedToman: 594_000 },
    { product: "هاست وردپرس پایه", domain: "mehrabcafe.ir", status: "Active", billingCycle: "Annually", channel: "code", startedDaysAgo: 16, earnedToman: 294_000 },
    { product: "هاست ووکامرس", domain: "shirinshop.ir", status: "Active", billingCycle: "Annually", channel: "link", startedDaysAgo: 11, earnedToman: 1_200_000 },
    { product: "آسمان آبی، پلن استارتر", domain: "pixelyar.com", status: "Active", billingCycle: "Quarterly", channel: "campaign", startedDaysAgo: 30, earnedToman: 78_000 },
    { product: "سرور مجازی ابری، ۲ گیگ", domain: "farazdev.ir", status: "Suspended", billingCycle: "Monthly", channel: "link", startedDaysAgo: 40, earnedToman: 77_500 },
    { product: "هاست وردپرس پایه", domain: "baharestan-co.ir", status: "Cancelled", billingCycle: "Annually", channel: "refcode", startedDaysAgo: 400, earnedToman: 462_000 },
    { product: "هاست وردپرس حرفه‌ای", domain: "ketabyar.ir", status: "Active", billingCycle: "Annually", channel: "code", startedDaysAgo: 52, earnedToman: 476_000 },
    { product: "سرور مجازی ابری، ۴ گیگ", domain: "mahtabco.com", status: "Active", billingCycle: "Semi-Annually", channel: "link", startedDaysAgo: 95, earnedToman: 725_000 },
    { product: "هاست وردپرس حرفه‌ای", domain: "danesh-yar.ir", status: "Active", billingCycle: "Annually", channel: "manual", startedDaysAgo: 24, earnedToman: 850_000 },
];

const services: PartnerServiceItem[] = serviceSeeds.map((s, i) => ({
    serviceId: 70_100 + i * 17,
    product: s.product,
    domainMasked: maskDomain(s.domain),
    status: s.status,
    statusLabel: WHMCS_STATUS_FA[s.status],
    billingCycle: s.billingCycle,
    channel: s.channel,
    channelLabel: CHANNEL_FA[s.channel],
    since: at(s.startedDaysAgo),
    earnedRial: (BigInt(s.earnedToman) * 10n).toString(),
    // Services older than the cutover came from the old system with part of their earnings.
    legacyEarnedRial: s.startedDaysAgo > 300 ? ((BigInt(s.earnedToman) * 10n * 7n) / 10n).toString() : "0",
    migrated: s.startedDaysAgo > 300,
    holdUntil: s.status === "Active" && s.startedDaysAgo < 14 ? at(s.startedDaysAgo - 14) : null,
}));

export function servicesMock(cursor: string | null, limit: number): PartnerServicesResponse {
    const start = cursor ? Number(cursor) : 0;
    return {
        items: structuredClone(services.slice(start, start + limit)),
        nextCursor: start + limit < services.length ? String(start + limit) : null,
        activeCount: services.filter((s) => s.status === "Active" && !s.holdUntil).length,
        inHoldCount: services.filter((s) => s.holdUntil).length,
    };
}

/* ---------------- claims ---------------- */

const CLAIM_DAYS = 30;

const CLAIM_COPY: Record<ClaimView["status"], { label: string; explanation: string; nextStep: string }> = {
    open: {
        label: "در حال بررسی",
        explanation: "ادعای شما ثبت شد و تیم میزبان‌فا نشانه‌های این سفارش (کلیک، کد و اتصال) را بررسی می‌کند.",
        nextStep: "کاری لازم نیست. نتیجه را همین‌جا می‌بینید؛ معمولاً تا چند روز کاری.",
    },
    approved: {
        label: "پذیرفته شد",
        explanation: "سفارش به نام شما ثبت شد و پورسانت آن مثل بقیه دورهٔ ضمانت را طی می‌کند.",
        nextStep: "پورسانت این سفارش را در فهرست سفارش‌ها دنبال کنید.",
    },
    rejected: {
        label: "پذیرفته نشد",
        explanation: "پس از بررسی، نشانه‌ای پیدا نشد که این سفارش از طریق شما آمده باشد.",
        nextStep: "اگر مدرکی دارید (مثلاً گفتگو با مشتری پیش از خرید)، از طریق پشتیبانی بفرستید.",
    },
};

const claim = (id: number, orderRef: string, orderId: number, status: ClaimView["status"], createdAt: string, decidedAt: string | null, adminNote: string | null = null): ClaimView => ({
    id,
    orderRef,
    orderId,
    status,
    statusLabel: CLAIM_COPY[status].label,
    explanation: CLAIM_COPY[status].explanation,
    nextStep: CLAIM_COPY[status].nextStep,
    adminNote: status === "rejected" ? adminNote : null,
    createdAt,
    decidedAt,
});

const claims: ClaimView[] = [
    claim(120, "48402", 48_402, "open", at(1), null),
    claim(118, "golbarg-gallery.ir", 48_377, "open", at(4), null),
    claim(111, "48211", 48_211, "approved", at(20), at(9)),
    claim(
        104,
        "48102",
        48_102,
        "rejected",
        at(33),
        at(26),
        "در ثبت سفارش هیچ کد یا کلیکی از شما دیده نشد و مشتری با کد کمپین عمومی خرید کرده است. در صورت داشتن شواهد تازه، با پشتیبانی بازاریاب‌ها در تماس باشید.",
    ),
];

export const claimsMock = (): PartnerClaimsResponse => ({
    items: structuredClone(claims).sort((a, b) => b.id - a.id),
    claimDays: CLAIM_DAYS,
    help: `اگر مشتری با معرفی شما خرید کرده ولی سفارش به نام شما ثبت نشده، تا ${fa(CLAIM_DAYS)} روز پس از سفارش، شمارهٔ سفارش یا دامنهٔ مشتری را وارد کنید تا بررسی کنیم.`,
});

/** Mock orders the claim form can find: by order id, or by the customer's (unmasked) domain. */
const KNOWN_ORDERS: { orderId: number; domain: string; daysAgo: number; self?: boolean }[] = [
    { orderId: 48_377, domain: "golbarg-gallery.ir", daysAgo: 9 },
    { orderId: 48_402, domain: "nikanmed.ir", daysAgo: 3 },
    { orderId: 48_415, domain: "atlasprint.ir", daysAgo: 6 },
    { orderId: 48_423, domain: "rahnama-tour.com", daysAgo: 12 },
    { orderId: 48_102, domain: "pardis-shop.ir", daysAgo: 40 },
    { orderId: 48_430, domain: "saramohammadi.ir", daysAgo: 14, self: true },
];

export function createClaimMock(body: CreateClaimRequest): ClaimView {
    const ref = body.ref.trim().toLowerCase().replace(/^#/, "").replace(/^www\./, "");
    if (ref.length < 3) throw new ApiError(400, "شمارهٔ سفارش یا دامنهٔ مشتری را وارد کنید.", "ref");
    if ((body.message ?? "").length > 1000) throw new ApiError(400, "توضیح حداکثر ۱۰۰۰ کاراکتر است.", "message");
    const fromList = commissions.find((c) => String(c.orderId) === ref);
    const order =
        KNOWN_ORDERS.find((o) => String(o.orderId) === ref || o.domain === ref) ??
        (fromList ? { orderId: fromList.orderId!, domain: "", daysAgo: Math.floor((now - new Date(fromList.createdAt).getTime()) / DAY) } : undefined);
    if (!order) throw new ApiError(404, "سفارشی با این شماره یا دامنه پیدا نشد. اگر سفارش همین امروز ثبت شده، چند ساعت دیگر دوباره امتحان کنید.", "ref");
    if (order.daysAgo > CLAIM_DAYS) throw new ApiError(422, `مهلت ثبت ادعا ${fa(CLAIM_DAYS)} روز پس از سفارش است و برای این سفارش تمام شده است.`, "ref");
    if ("self" in order && order.self) throw new ApiError(422, "این سفارش با حساب کاربری خود شما ثبت شده است و خرید برای خودتان پورسانت ندارد.", "ref");
    if (fromList && fromList.status !== "void" && fromList.status !== "reversed") {
        throw new ApiError(409, "این سفارش همین حالا به نام شما ثبت شده است؛ آن را در فهرست سفارش‌ها می‌بینید.", "ref");
    }
    if (claims.some((c) => c.orderId === order.orderId && c.status !== "rejected")) throw new ApiError(409, "برای این سفارش قبلاً ادعا ثبت کرده‌اید.", "ref");

    const created = claim(Math.max(...claims.map((c) => c.id)) + 1, body.ref.trim(), order.orderId, "open", new Date().toISOString(), null);
    claims.unshift(created);
    return structuredClone(created);
}

const LEGACY_DOMAINS = ["ketab-sara.ir", "nikmehr.com", "shop-arya.ir", "dr-salehi.co.ir", "mehr-tour.net"];

function legacyRows(f: LegacyCommissionsFilter): LegacyCommissionItem[] {
    const DOMAINS = LEGACY_DOMAINS;
    const all: LegacyCommissionItem[] = Array.from({ length: 46 }, (_, i) => {
        const at = new Date(Date.now() - (380 + i * 17) * 86_400_000);
        at.setUTCHours(8, 30, 0, 0);
        const kind = i % 11 === 10 ? null : i % 3 === 0 ? ("new" as const) : ("renewal" as const);
        return {
            id: String(70_000 + i),
            occurredAt: at.toISOString(),
            occurredAtJalali: formatJalali(at),
            amountRial: String((kind === "new" ? 185_000 + (i % 5) * 41_000 : 64_000 + (i % 4) * 12_500) * 10),
            kind,
            kindLabel: kind === "new" ? "فروش جدید" : kind === "renewal" ? "تمدید" : "نامشخص",
            domainMasked: kind ? maskDomain(DOMAINS[i % DOMAINS.length]!) : null,
            invoiceId: 380_000 + i * 13,
            serviceId: kind ? 61_000 + (i % DOMAINS.length) * 7 : null,
            dateEstimated: i === 7,
        };
    });
    const q = (f.q ?? "").trim().toLowerCase();
    return all.filter((r) => {
        const day = r.occurredAt.slice(0, 10);
        if (f.from && day < f.from) return false;
        if (f.to && day > f.to) return false;
        if (f.kind && r.kind !== f.kind) return false;
        if (q && !(String(r.serviceId) === q || String(r.invoiceId) === q || (r.domainMasked ?? "").includes(q))) return false;
        return true;
    });
}

/** `GET /legacy-commissions?page&from&to&kind&q` mock: the old WHMCS history (newest first, 20 per page). */
export function legacyCommissionsMock(page: number, f: LegacyCommissionsFilter = {}): LegacyCommissionsResponse {
    const size = 20;
    const all = legacyRows(f);
    return {
        items: all.slice((page - 1) * size, page * size),
        page,
        pageSize: size,
        total: all.length,
        totalRial: all.reduce((s, r) => s + BigInt(r.amountRial), 0n).toString(),
        firstAt: all[all.length - 1]?.occurredAt ?? null,
        lastAt: all[0]?.occurredAt ?? null,
    };
}

/** `GET /legacy-commissions/export` mock. */
export function legacyCommissionsExportMock(f: LegacyCommissionsFilter = {}): LegacyCommissionsExport {
    return { items: legacyRows(f), truncated: false };
}

/** `GET /legacy-payouts` mock: commission moved to the WHMCS account credit. */
export function legacyPayoutsMock(): LegacyPayoutsResponse {
    const rows = [
        { days: 420, toman: 1_250_000 },
        { days: 610, toman: 640_000 },
        { days: 900, toman: 255_000 },
    ].map((r, i) => {
        const at = new Date(Date.now() - r.days * 86_400_000);
        at.setUTCHours(8, 30, 0, 0);
        return { id: String(9_000 + i), occurredAt: at.toISOString(), occurredAtJalali: formatJalali(at), amountRial: String(r.toman * 10), kindLabel: "انتقال به کیف پول", description: null };
    });
    return { items: rows, total: rows.length, totalRial: rows.reduce((s, r) => s + BigInt(r.amountRial), 0n).toString() };
}
